import {
    Alert,
    BulletList,
    CheckBox,
    ClearIcon,
    Column,
    Container,
    cssVar,
    Header,
    Label,
    PrimaryButton,
    SecondaryButton,
    Splash,
    Tabbed,
    ToolTip,
    useAlignments,
    useScreen,
    useSizes,
} from 'metrix-common3';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { getAndUseRoute } from '../../../utils';
import ProductIcons from '../../ProductClassIcon';
import ProfileRenewalMenu from '../CommonComponents/Menu';
import ProfileRenewalQuotes from '../CommonComponents/Quotes';
import ProfileRenewalRecap from '../CommonComponents/Recap';
import ProfileRenewalRisks from '../CommonComponents/Risks';
import Signature from '../CommonComponents/Signature';
import ProfileRenewalAction from './Action';
import ProfileRenewalHeader from './Header';
import CustomSlider from './Slider';

const NotificationContainer = styled(Container)`
    border-radius: 2px;
    align-items: center;
`;

const NotificationTextContainer = styled(Container)`
    align-items: center;
`;

const ClearContainer = styled(Container)`
    cursor: pointer;
`;

const InfoContainer = styled(Container)`
    border: 1px solid ${cssVar.colorStrokeLight};
    border-radius: 4px;
`;

const DecisionButton = styled(Container)`
    justify-content: center;
`;

const LandingContainer = styled(Container)`
    border: 1px solid ${cssVar.colorStrokeLight};
    border-radius: 2px;
    justify-content: space-around;
    box-shadow: 5px 10px 15px ${cssVar.colorStrokeDark};
`;

const ProfileRenewal = (props) => {
    const alignments = useAlignments();
    const sizes = useSizes();
    const screen = useScreen();

    const [displayNotification, setDisplayNotification] = useState(true);
    const [statusValue, setStatusValue] = useState(0);
    const [documentAccepted, setDocumentAccepted] = useState(false);
    const [displaySignaturePad, setDisplaySignaturePad] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only init
    useEffect(() => {
        if (props.policySummary) {
            const clientStatus = props.policySummary?.status;
            updateProgressBar(clientStatus);
        }
    }, []);

    const getOperationMessages = () => {
        if (
            !props.context.operation ||
            (props.context.operation &&
                !props.context.operation.errors &&
                !props.context.operation.warnings &&
                !props.context.operation.confirmations)
        ) {
            return null;
        }

        const hasErrors = props.context.operation.errors && props.context.operation.errors.length > 0;
        const hasWarnings = props.context.operation.warnings && props.context.operation.warnings.length > 0;
        const hasConfirmations =
            props.context.operation.confirmations && props.context.operation.confirmations.length > 0;

        const mode = hasErrors ? 'danger' : hasWarnings ? 'info' : '';
        const header = hasErrors ? (
            <FormattedMessage id='Profile_Renewal.errorMessage' />
        ) : hasWarnings ? (
            <FormattedMessage id='Profile_Renewal.warning' />
        ) : null;

        const messages = [];

        if (hasErrors) {
            props.context.operation.errors.forEach((item) => {
                messages.push({ code: item.code, value: item.value ? item.value : item.code, isError: true });
            });
        }

        if (hasWarnings) {
            props.context.operation.warnings.forEach((item) => {
                messages.push({ code: item.code, value: item.value ? item.value : item.code, isError: false });
            });
        }

        if (hasConfirmations) {
            props.context.operation.confirmations.forEach((item) => {
                messages.push({ code: item.code, value: item.value ? item.value : item.code, isError: false });
            });
        }

        return <Alert mode={mode} header={header} items={messages} />;
    };

    const getNotifyMessage = (status, isClient, clientName) => {
        let notificationText = '';
        let messageColor = cssVar.colorSuccess;

        if (isClient) {
            switch (status) {
                case 'quoted':
                case 'incomplete':
                    notificationText = (
                        <FormattedMessage id='Profile_Renewal.notifications.review' values={{ clientName }} />
                    );
                    messageColor = cssVar.colorError;
                    break;
                case 'quoteIssued':
                    notificationText = <FormattedMessage id='Profile_Renewal.notifications.reviewAccept' />;
                    break;
                case 'quoteAccepted':
                    notificationText = <FormattedMessage id='Profile_Renewal.notifications.readyToSign' />;
                    messageColor = cssVar.colorError;
                    break;
                case 'bindRequested':
                case 'bound':
                    notificationText = <FormattedMessage id='Profile_Renewal.notifications.workingOnPolicy' />;
                    break;
                case 'issued':
                    notificationText = <FormattedMessage id='Profile_Renewal.notifications.issuedByBroker' />;
                    break;
                default:
                    notificationText = '';
                    break;
            }
        } else {
            switch (status) {
                case 'incomplete':
                    notificationText = (
                        <FormattedMessage
                            id='Profile_Renewal.notifications.broker.pendingReview'
                            values={{ clientName }}
                        />
                    );
                    break;
                case 'quoted':
                    notificationText = (
                        <FormattedMessage
                            id='Profile_Renewal.notifications.broker.needsIssued'
                            values={{ clientName }}
                        />
                    );
                    break;
                case 'quoteIssued':
                    notificationText = (
                        <FormattedMessage
                            id='Profile_Renewal.notifications.broker.waitingAccept'
                            values={{ clientName }}
                        />
                    );
                    break;
                case 'quoteAccepted':
                    notificationText = (
                        <FormattedMessage
                            id='Profile_Renewal.notifications.broker.quoteAccepted'
                            values={{ clientName }}
                        />
                    );
                    break;
                case 'bindRequested':
                    notificationText = (
                        <FormattedMessage
                            id='Profile_Renewal.notifications.broker.waitingBind'
                            values={{ clientName }}
                        />
                    );
                    break;
                case 'bound':
                    notificationText = <FormattedMessage id='Profile_Renewal.notifications.broker.finalizePolicy' />;
                    break;
                default:
                    notificationText = '';
                    break;
            }
        }

        return { notificationText, messageColor };
    };

    const updateProgressBar = (clientStatus) => {
        if (clientStatus === 'quoted' || clientStatus === 'quoteIssued') {
            setStatusValue(50);
        } else if (clientStatus === 'quoteAccepted') {
            setStatusValue(75);
        } else if (clientStatus === 'bound' || clientStatus === 'issued' || clientStatus === 'bindRequested') {
            setStatusValue(100);
        } else {
            setStatusValue(25);
        }
    };

    const closeSignaturePad = () => {
        setDisplaySignaturePad(false);
    };

    const openSignaturePad = () => {
        setDisplaySignaturePad(true);
    };

    const handleContinueQuoteClick = async (e, payload) => {
        await getAndUseRoute(e, props.context.session, payload);
    };

    const baseProps = {
        displayNotification,
        statusValue,
        documentAccepted,
        displaySignaturePad,
        ...props,
    };

    const isMobile = screen?.isMobile;
    const _isDesktop = props.isDesktop;
    const _isTablet = props.isTablet;

    const _isAdmin = props.context.session.userType === 'administrator';
    const _isBroker = props.context.session.userType === 'broker';
    const isClient = props.context.session.userType === 'client';

    const clientName = props.policySummary?.participants?.insured?.party?.name?.firstName;
    const clientStatus = props.policySummary?.status;
    const expiryDate = props.policySummary.expiresInDays;
    const { notificationText, messageColor } = getNotifyMessage(clientStatus, isClient, clientName, expiryDate);

    const productName = props.policySummary.productName;
    const productClass = props.policySummary.productClass;

    const riskChangeSummary = props.policySummary.riskChangeSummary;

    // This was hardcoded before but now uses internationalized messages
    const labels = [
        <FormattedMessage key='renewalReview' id='Profile_Renewal.renewalReview' />,
        <FormattedMessage key='acceptQuote' id='Profile_Renewal.acceptQuote' />,
        <FormattedMessage key='signDocs' id='Profile_Renewal.signDocs' />,
    ];

    const productTabs = [
        { code: 'Home', descdescription: <FormattedMessage id='Profile_Renewal.homeOwner' /> },
        { code: 'Landlord', descdescription: <FormattedMessage id='Profile_Renewal.landlordOwner' /> },
        { code: 'RV', descdescription: <FormattedMessage id='Profile_Renewal.rvOwner' /> },
    ];

    return (
        props.policySummary && (
            <Splash color={cssVar.colorLightMode}>
                <ProfileRenewalHeader {...props} />
                <Container top={sizes.md} right={sizes.xs} left={sizes.xs}>
                    {/*Landing Container from email to redirect*/}
                    {clientStatus === 'incomplete' && isClient && (
                        <LandingContainer inline>
                            <Container top={sizes.md} bottom={sizes.md}>
                                <ProductIcons productClass={productClass} size={sizes.lg} />
                            </Container>
                            <Header
                                text={<FormattedMessage id='Profile_Renewal.yourInsurance' values={{ productName }} />}
                                color={cssVar.colorPrimary}
                                size={sizes.md}
                                top={sizes.md}
                                bottom={sizes.md}
                            />
                            <PrimaryButton
                                text={<FormattedMessage id='Profile_Renewal.reviewRenewal' />}
                                clickEvent={(e) =>
                                    handleContinueQuoteClick(e, {
                                        siteCode: 'corporateWeb',
                                        path: 'workflow',
                                        entity: props.policySummary.guid,
                                        isBlankPage: true,
                                    })
                                }
                                right={sizes.xs}
                                top={sizes.md}
                                bottom={sizes.md}
                            />
                        </LandingContainer>
                    )}

                    {/*Document Acceptance and Sign Document section*/}
                    {clientStatus === 'quoteAccepted' && isClient && (
                        <Column xs={isMobile ? 12 : 11}>
                            <CheckBox
                                label={<FormattedMessage id='Profile_Renewal.documentAcceptance' />}
                                value={documentAccepted}
                                clickEvent={(_value) => setDocumentAccepted(!documentAccepted)}
                                bottom={sizes.md}
                            />

                            <DecisionButton inline>
                                <PrimaryButton
                                    clickEvent={() => props.changeTabTo('documents')}
                                    text={<FormattedMessage id='Profile_Renewal.downloadQuote' />}
                                    right={sizes.xs}
                                />
                                <SecondaryButton
                                    text={<FormattedMessage id='Profile_Renewal.signDocuments' />}
                                    disabled={!documentAccepted}
                                    clickEvent={() => openSignaturePad()}
                                />
                            </DecisionButton>
                            {displaySignaturePad && documentAccepted && (
                                <Signature
                                    closePad={() => closeSignaturePad()}
                                    updateProgressBar={(status) => updateProgressBar(status)}
                                    {...baseProps}
                                />
                            )}
                        </Column>
                    )}

                    {(props.context.session.userType === 'broker' ||
                        props.context.session.userType === 'administrator') && (
                        <React.Fragment>
                            <ProfileRenewalMenu {...props} />
                            {getOperationMessages()}
                            {props.policySummary.messages && props.policySummary.messages.length > 0 && (
                                <Alert
                                    header={<FormattedMessage id='Profile_Renewal.attention' />}
                                    items={props.policySummary.messages}
                                />
                            )}
                        </React.Fragment>
                    )}
                    {isClient ? (
                        <>
                            <Header
                                text={<FormattedMessage id='Profile_Renewal.chooseYourRenewal' />}
                                textAlign={alignments.left}
                                size={sizes.lg}
                                top={sizes.lg}
                            />
                            <Label
                                text={<FormattedMessage id='Profile_Renewal.reviewPoliciesInfo' />}
                                textAlign={alignments.left}
                                bottom={sizes.md}
                            />
                            {isMobile ? (
                                <Column xs={12}>
                                    <CustomSlider
                                        type='ratio'
                                        Labels={labels}
                                        // biome-ignore lint/suspicious/noEmptyBlockStatements: noop callback for changeEvent contract (read-only slider)
                                        changeEvent={() => {}}
                                        leftLabel={<FormattedMessage id='Profile_Renewal.start' />}
                                        rightLabel={<FormattedMessage id='Profile_Renewal.finish' />}
                                        value={statusValue}
                                        isMobile={isMobile}
                                        {...props}
                                    />
                                </Column>
                            ) : (
                                <Column md={12}>
                                    <CustomSlider
                                        type='ratio'
                                        Labels={labels}
                                        // biome-ignore lint/suspicious/noEmptyBlockStatements: noop callback for changeEvent contract (read-only slider)
                                        changeEvent={() => {}}
                                        leftLabel={<FormattedMessage id='Profile_Renewal.start' />}
                                        rightLabel={<FormattedMessage id='Profile_Renewal.finish' />}
                                        value={statusValue}
                                    />
                                </Column>
                            )}
                        </>
                    ) : (
                        <>
                            <Header
                                text={<FormattedMessage id='Profile_Renewal.insuranceFor' values={{ clientName }} />}
                                textAlign={alignments.left}
                                size={sizes.lg}
                                top={sizes.lg}
                            />
                            <Label
                                text={<FormattedMessage id='Profile_Renewal.reviewCustomerInfo' />}
                                textAlign={alignments.left}
                            />
                        </>
                    )}
                    {isMobile
                        ? notificationText !== '' &&
                          displayNotification && (
                              <NotificationContainer color={messageColor} top={sizes.md} inline>
                                  <Column xs={11}>
                                      <NotificationTextContainer inline>
                                          <ToolTip color={cssVar.colorLightMode}>{notificationText}</ToolTip>
                                          {notificationText}
                                      </NotificationTextContainer>
                                  </Column>

                                  <Column xs={1} nogutterleft nogutterright>
                                      <ClearContainer clickEvent={() => setDisplayNotification(false)}>
                                          <ClearIcon color={cssVar.colorLightMode} />
                                      </ClearContainer>
                                  </Column>
                              </NotificationContainer>
                          )
                        : notificationText !== '' &&
                          displayNotification && (
                              <NotificationContainer color={messageColor} top={sizes.md} inline>
                                  <Column md={11}>
                                      <NotificationTextContainer inline>
                                          <ToolTip color={cssVar.colorLightMode}>{notificationText}</ToolTip>
                                          {notificationText}
                                      </NotificationTextContainer>
                                  </Column>

                                  <Column md={1}>
                                      <ClearContainer clickEvent={() => setDisplayNotification(false)}>
                                          <ClearIcon color={cssVar.colorLightMode} />
                                      </ClearContainer>
                                  </Column>
                              </NotificationContainer>
                          )}
                    {isClient && (
                        <>
                            <Tabbed items={productTabs} descriptionSize={sizes.md} lg={4} />
                            <InfoContainer>
                                <Header
                                    text={<FormattedMessage id='Profile_Renewal.shopRenewalTitle' />}
                                    textAlign={alignments.left}
                                    size={sizes.md}
                                    left={sizes.sm}
                                    top={sizes.sm}
                                />
                                <Label
                                    text={<FormattedMessage id='Profile_Renewal.shopRenewalDescription' />}
                                    textAlign={alignments.left}
                                    left={sizes.sm}
                                    bottom={sizes.sm}
                                />
                            </InfoContainer>
                        </>
                    )}
                    {riskChangeSummary && (
                        <InfoContainer top={sizes.md}>
                            <Header
                                text={<FormattedMessage id='Profile_Renewal.riskChangeSummary' />}
                                textAlign={alignments.left}
                                size={sizes.md}
                                left={sizes.sm}
                                top={sizes.sm}
                            />
                            <BulletList
                                references={riskChangeSummary.map((summary) => ({
                                    code: Math.random().toString(),
                                    value: summary,
                                }))}
                                size={sizes.md}
                            />
                        </InfoContainer>
                    )}

                    <ProfileRenewalAction {...props} />
                    {(props.policySummary.status === 'quoteAccepted' ||
                        props.policySummary.status === 'bindRequested' ||
                        props.policySummary.status === 'bound' ||
                        props.policySummary.status === 'issued') && <ProfileRenewalRecap {...props} />}

                    <ProfileRenewalRisks {...props} risks={props.policySummary.risks} />
                    {(props.policySummary.status === 'quoted' ||
                        props.policySummary.status === 'quoteIssued' ||
                        props.policySummary.status === 'referred') && (
                        <ProfileRenewalQuotes {...props} updateProgressBar={(status) => updateProgressBar(status)} />
                    )}
                </Container>
            </Splash>
        )
    );
};

export default ProfileRenewal;
